import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, AppBar, Toolbar, Typography, Container, Tabs, Tab, Box, Paper, Button, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid, Alert } from '@mui/material';
import { Logout, Person, AccountCircle, LibraryBooks } from '@mui/icons-material';
import { Routes, Route, Navigate, useLocation, useNavigate, Link } from 'react-router';
import apiClient, { clearCsrfToken } from './utils/axiosConfig';
import Accounts from './components/Accounts';
import Manufacturers from './components/Manufacturers';
import MonitoredAccounts from './components/MonitoredAccounts';
import AccountOnboarding from './components/AccountOnboarding';
import UserManagement from './components/UserManagement';
import GeographicDashboard from './components/GeographicDashboard';
import LinkLibrary from './components/LinkLibrary';
import ProtectedRoute from './components/ProtectedRoute';
import Breadcrumbs from './components/Breadcrumbs';
import RouteLoader from './components/RouteLoader';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import Login from './components/Login';

const theme = createTheme({
  palette: {
    primary: {
      main: '#94BA33',
    },
  },
});

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileData, setProfileData] = useState({ full_name: '', current_password: '', new_password: '', confirm_password: '' });
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();

  const routes = [
    { path: '/onboarding', label: 'Account Onboarding' },
    { path: '/accounts', label: 'Accounts' },
    { path: '/manufacturers', label: 'Manufacturers' },
    { path: '/monitored-accounts', label: 'Monitored Accounts' },
    { path: '/geographic', label: 'Analytics' },
    ...(user?.role === 'admin' ? [{ path: '/users', label: 'User Management' }] : [])
  ];

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await apiClient.get('/api/verify-token');
        setUser(response.data.user);
      } catch (error) {
        console.warn('Token verification failed:', error);
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  };

  const handleLogin = (userData) => {
    setUser(userData);
    setAnchorEl(null);
    navigate('/onboarding');
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileDialogOpen = () => {
    setProfileData({ 
      full_name: user.full_name || '', 
      current_password: '', 
      new_password: '', 
      confirm_password: '' 
    });
    setProfileError('');
    setProfileSuccess('');
    setProfileDialogOpen(true);
    handleProfileMenuClose();
  };

  const handleProfileDialogClose = () => {
    setProfileDialogOpen(false);
    setProfileData({ full_name: '', current_password: '', new_password: '', confirm_password: '' });
    setProfileError('');
    setProfileSuccess('');
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');
    setProfileLoading(true);

    try {
      if (profileData.new_password || profileData.confirm_password) {
        if (!profileData.current_password) {
          setProfileError('Current password is required to change password');
          return;
        }
        if (profileData.new_password !== profileData.confirm_password) {
          setProfileError('New passwords do not match');
          return;
        }
        if (profileData.new_password.length < 14) {
          setProfileError('New password must be at least 14 characters long');
          return;
        }
      }

      const updateData = { full_name: profileData.full_name };
      if (profileData.new_password) {
        updateData.current_password = profileData.current_password;
        updateData.new_password = profileData.new_password;
      }

      await apiClient.put('/api/profile', updateData);
      
      setProfileSuccess('Profile updated successfully');
      setProfileData({ ...profileData, current_password: '', new_password: '', confirm_password: '' });
      
      setTimeout(() => {
        handleProfileDialogClose();
        window.location.reload();
      }, 1500);
      
    } catch (error) {
      setProfileError(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await apiClient.post('/api/logout', {});
      }
    } catch (error) {
      console.warn('Logout API call failed:', error.message);
    } finally {
      localStorage.removeItem('token');
      clearCsrfToken();
      setUser(null);
      setAnchorEl(null);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login onLogin={handleLogin} />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar position="sticky" sx={{ backgroundColor: 'white', color: 'black', boxShadow: 3 }}>
        <Toolbar>
          <Box sx={{ 
            backgroundColor: 'white', 
            borderRadius: 1, 
            p: 0.5, 
            mr: 2,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer'
          }}
          onClick={() => navigate('/onboarding')}
          >
            <img 
              src="/thumbnail_image.png" 
              alt="Logo" 
              style={{ height: 32, display: 'block' }}
            />
          </Box>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Account Dashboard
          </Typography>
          <Button
            color="inherit"
            startIcon={<LibraryBooks />}
            onClick={() => navigate('/links')}
            sx={{ mr: 2, textTransform: 'none' }}
          >
            Link Library
          </Button>
          <Button
            color="inherit"
            startIcon={<AccountCircle />}
            onClick={handleProfileMenuOpen}
            sx={{ mr: 1, textTransform: 'none' }}
          >
            {user.full_name ? user.full_name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ') : user.username}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleProfileDialogOpen}>
              <Person sx={{ mr: 1 }} /> Edit Profile
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <Logout sx={{ mr: 1 }} /> Logout
            </MenuItem>
          </Menu>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
            ({user.role === 'account_onboarding' ? 'Account Onboarding' : user.role.charAt(0).toUpperCase() + user.role.slice(1)})
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
        <Paper sx={{ mb: 3, position: 'sticky', top: 64, zIndex: 1000, backgroundColor: 'white' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex' }}>
            {routes.map((route) => (
              <Link 
                key={route.path} 
                to={route.path}
                style={{ textDecoration: 'none' }}
              >
                <Button
                  sx={{
                    minHeight: 48,
                    px: 3,
                    py: 1.5,
                    borderRadius: 0,
                    textTransform: 'none',
                    fontWeight: 500,
                    color: location.pathname === route.path ? 'primary.main' : 'text.primary',
                    backgroundColor: location.pathname === route.path ? 'primary.50' : 'transparent',
                    borderBottom: location.pathname === route.path ? 2 : 0,
                    borderBottomColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: location.pathname === route.path ? 'primary.50' : 'action.hover'
                    }
                  }}
                >
                  {route.label}
                </Button>
              </Link>
            ))}
          </Box>
        </Paper>
        
        <Breadcrumbs />
        
        <RouteErrorBoundary>
          <RouteLoader>
            <Box sx={{ p: 0, m:0 }}>
              <Routes>
                <Route path="/" element={<Navigate to="/onboarding" replace />} />
                <Route path="/onboarding" element={<AccountOnboarding />} />
                <Route path="/onboarding/:id" element={<AccountOnboarding />} />
                <Route path="/accounts" element={<Accounts />} />
                <Route path="/accounts/:id" element={<Accounts />} />
                <Route path="/geographic" element={<GeographicDashboard />} />
                <Route path="/links" element={<LinkLibrary />} />
                <Route path="/manufacturers" element={<Manufacturers />} />
                <Route path="/manufacturers/:id" element={<Manufacturers />} />
                <Route path="/monitored-accounts" element={<MonitoredAccounts />} />
                <Route path="/monitored-accounts/:id" element={<MonitoredAccounts />} />
                <Route 
                  path="/users" 
                  element={
                    <ProtectedRoute user={user} requiredRole="admin">
                      <UserManagement />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/users/:id" 
                  element={
                    <ProtectedRoute user={user} requiredRole="admin">
                      <UserManagement />
                    </ProtectedRoute>
                  } 
                />
                <Route path="*" element={<Navigate to="/onboarding" replace />} />
              </Routes>
            </Box>
          </RouteLoader>
        </RouteErrorBoundary>
      </Container>

      {/* Profile Dialog */}
      <Dialog open={profileDialogOpen} onClose={handleProfileDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <Person sx={{ mr: 1 }} /> Edit Profile
        </DialogTitle>
        <form onSubmit={handleProfileUpdate}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Email"
                  value={user.username}
                  disabled
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={profileData.full_name}
                  onChange={(e) => setProfileData({...profileData, full_name: e.target.value})}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Change Password (Optional)
                </Typography>
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Current Password"
                  type="password"
                  value={profileData.current_password}
                  onChange={(e) => setProfileData({...profileData, current_password: e.target.value})}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="New Password"
                  type="password"
                  value={profileData.new_password}
                  onChange={(e) => setProfileData({...profileData, new_password: e.target.value})}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type="password"
                  value={profileData.confirm_password}
                  onChange={(e) => setProfileData({...profileData, confirm_password: e.target.value})}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              {profileError && (
                <Grid size={12}>
                  <Alert severity="error">{profileError}</Alert>
                </Grid>
              )}
              {profileSuccess && (
                <Grid size={12}>
                  <Alert severity="success">{profileSuccess}</Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleProfileDialogClose} disabled={profileLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={profileLoading}>
              {profileLoading ? 'Updating...' : 'Update Profile'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </ThemeProvider>
  );
}

export default App;